<template>
  <v-card-text v-if="mobile">
    <div class="d-flex align-center justify-space-between mb-3 pb-1">
      <span class="text-subtitle-1 text-left d-flex flex-column">
        <span class="font-weight-bold">
          {{ ticket.name.toUpperCase() }}
        </span>
      </span>

      <span v-if="price !== null" class="text-subtitle-1 text-right d-flex flex-column">
        {{ currency(price) }}
      </span>
    </div>

    <v-row align="center" justify="space-between">
      <v-col cols="auto">
        <span class="text-subtitle-1">
          Taxa de Serviço:
        </span>
      </v-col>
      <v-col>
        {{ currency(fee) }}
      </v-col>
    </v-row>

    <v-row v-if="!settings.match(event, 'HIDE_BATCH_NUMBER')" align="center" justify="space-between">
      <v-col cols="auto">
        <span class="text-subtitle-1">
          Lote:
        </span>
      </v-col>
      <v-col>
        {{ displayBatch?.number }}
      </v-col>
    </v-row>

    <v-row align="center" justify="space-between">
      <v-col cols="auto">
        Quantidade:
      </v-col>

      <v-col>
        <v-btn v-if="unavailable || soldOut" block disabled>
          Esgotado
        </v-btn>
        <v-btn
          v-else-if="accessState === 'not_linked' || accessState === 'plan_mismatch' || accessState === 'misconfigured'"
          block
          :color="ticket.integration_tiers[0].integration.primary_color"
          :style="{ color: ticket.integration_tiers[0].integration.secondary_color }"
          @click="onAccessButtonClick"
        >
          <div class="d-flex flex-column">
            <small style="font-size: 0.6em;">Venda exclusiva</small>
            <template v-if="ticket.integration_tiers.length > 1">
              {{ ticket.integration_tiers[0].integration.name }}
            </template>
            <template v-else>
              {{ ticket.integration_tiers[0].name }}
            </template>
          </div>
        </v-btn>
        <t-number-input
          v-else
          v-model="amount"
          hide-details
          density="compact"
          :max="max"
          @incremented="dispatchAnalytics('add')"
          @decremented="dispatchAnalytics('remove')"
        />
      </v-col>
    </v-row>
  </v-card-text>

  <tr v-else class="border-dashed">
    <td class="text-subtitle-1">
      {{ ticket.name.toUpperCase() }}
    </td>

    <td v-if="!settings.match(event, 'HIDE_BATCH_NUMBER')" class="text-center">
      {{ displayBatch?.number }}
    </td>

    <td style="width: 15%" class="text-subtitle-1 text-center">
      <template v-if="price !== null">
        {{ currency(price) }}
      </template>
    </td>

    <td style="width: 15%" class="text-subtitle-1 text-center">
      {{ currency(fee) }}
    </td>

    <td style="width: 15%" class="py-2">
      <v-btn v-if="unavailable || soldOut" block disabled>
        Esgotado
      </v-btn>
      <v-btn
        v-else-if="accessState === 'not_linked' || accessState === 'plan_mismatch' || accessState === 'misconfigured'"
        block
        :color="ticket.integration_tiers[0].integration.primary_color"
        :style="{ color: ticket.integration_tiers[0].integration.secondary_color }"
        @click="onAccessButtonClick"
      >
        <div class="d-flex flex-column">
          <small style="font-size: 0.6em;">Venda exclusiva</small>
          <template v-if="ticket.integration_tiers.length > 1">
            {{ ticket.integration_tiers[0].integration.name }}
          </template>
          <template v-else>
            {{ ticket.integration_tiers[0].name }}
          </template>
        </div>
      </v-btn>
      <t-number-input
        v-else
        v-model="amount"
        hide-details
        density="compact"
        :max="max"
        @incremented="dispatchAnalytics('add')"
        @decremented="dispatchAnalytics('remove')"
      />
    </td>
  </tr>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { useDisplay } from "vuetify";
import TNumberInput from "@/components/core/TNumberInput.vue";
import type TicketType from "@/types/TicketType";
import type Event from "@/types/Event";
import type TicketBatch from "@/types/TicketBatch";
import type Sector from "@/types/Sector";
import { TrackableEvent } from "@/analytics/TrackableEvents";
import { useAnalytics } from "@/plugins/analytics";
import { useCartStore } from "@/stores/cart";
import { useTicketAccess } from "@/composables/useTicketAccess";
import { currency } from "@/filters";
import Fee from "@/utils/fees";
import { isSoldOut } from "@/utils/ticketType";
import Section from "@/types/Section";
import { useSettings } from "@/plugins/settings";

interface Props {
  sector: Sector;
  section: Section;
  ticket: TicketType;
  modelValue?: number;
  event: Event;
}

const DEFAULT_MAX = 99999;

type Emits = (e: "update:modelValue", value: number) => void;

const { sector, section, ticket, modelValue = 0, event } = defineProps<Props>();

const emit = defineEmits<Emits>();

const cart = useCartStore();
const settings = useSettings();
const { mobile } = useDisplay();
const analytics = useAnalytics();

const batch = computed<TicketBatch | null>(() => ticket.current_batch ?? null);

const displayBatch = computed<TicketBatch | null>(() => ticket.display_batch ?? null);

/** Null renders no price at all. */
const price = computed<number | null>(() => displayBatch.value?.price ?? null);

/**
 * The channel has no current batch: every batch is closed, or the next one is
 * scheduled and its period has not opened. Kept separate from `soldOut` because
 * it also drives `max`, but both render "Esgotado".
 */
const unavailable = computed<boolean>(() => batch.value === null);

/** Convenience fee charged for a single unit of this ticket type. */
const fee = computed<number>(() => Fee.calculate(price.value, event.convenience_fee, event.convenience_fee_mode));

/** The current batch is exhausted, or the sector pool is. */
const soldOut = computed<boolean>(() => isSoldOut(sector, ticket));

/**
 * This function is kinda heavy on business logic so i want to give some context. Yes, the function if really ugly, feel free to
 * improve it if you can make it pass the tests.
 *
 * There are 3 limiters that affect the max number of tickets a user can select for a given type, with different scopes.
 *
 * ### TicketsPerUserLimiter
 *
 * This is global, that means that changes in the whole cart (apart from this input) affect the max for each input.
 *
 * This represents the max number of tickets a single user can buy for the event.
 *
 * If the limit is set at 10, and this is Type C, and the user has already selected 4 of Type A and 3 of Type B,
 * then the max for Type C will be 3.
 *
 * ### SeatLimiter
 *
 * This one is regulated per section, that means that changes in other sections do not affect the max for this input.
 *
 * This represents the number of seats on the whole section with available status for this event.
 *
 * If the base value for available seats is 10 and this it type A1, if the user has selected 4 tickets on Type A2 from the
 * same section, then there's 6 max tickets for Type B. But if user now selects another ticket on Type B that is from another
 * section then the max for Type A1 remains 6.
 *
 * ### TicketLimiter
 *
 * The last one is simpler and scoped by ticket type, that means that changes in other ticket types do not affect the max for this input.
 *
 * This represents the number of remaining tickets on this type for all users and it's calculated by the batch's remaining capacity.
 */
const max = computed<number>(() => {
  if (unavailable.value) return 0;

  const ticketsPerUserLimiter = event.quantidade_ingresso_venda_online - cart.totalItems + modelValue;

  const batchQuantity = batch.value?.quantity ?? null;

  const ticketTypeLimiter = batchQuantity === null
    ? DEFAULT_MAX
    : batchQuantity - (batch.value?.sold ?? 0);

  const sectorLimiter = sector.capacity - sector.sold;

  const maxTicketsPerUser = ticket.max_per_user ?? DEFAULT_MAX;

  const sectionCounter = sector.sections.find(s => s.id === section.id)?.tickets.reduce(
    (acc, t) => acc + t.quantidade,
    0,
  );

  let seatLimiter;

  if (section.seats === undefined || section.seats.total === 0) {
    seatLimiter = DEFAULT_MAX;
  } else {
    seatLimiter = section.seats.available - (sectionCounter ?? 0) + modelValue;
  }

  return Math.max(0, Math.min(ticketsPerUserLimiter, ticketTypeLimiter, sectorLimiter, seatLimiter, maxTicketsPerUser));
});

const { accessState, onAccessButtonClick } = useTicketAccess(() => ticket, () => event);

const amount = computed<number>({
  get(): number {
    return modelValue;
  },
  set(amount: number) {
    emit("update:modelValue", amount);
  },
});

const dispatchAnalytics = (action: "add" | "remove") => {
  analytics.collect(action == "add" ? TrackableEvent.AddToCart : TrackableEvent.RemoveFromCart, {
    producer_id: event.cliente_id,
    params: {
      currency: "BRL",
      items: {
        item_name: ticket.name,
        item_id: ticket.id,
        price: ticket.price,
        quantity: 1,
      },
    },
  });
  return true;
};

watch(
  () => soldOut.value || unavailable.value,
  (newValue) => {
    if (newValue === true) {
      amount.value = 0;
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #ccc;
}

.border-dashed td,
.border-dashed {
  border-bottom: 1px dashed #ccc !important;
}

.border-dashed:last-child,
.border-dashed:last-child > td {
  border-bottom: none !important;
}
</style>
